import { Component } from '@theme/component';

/**
 * Size map for EU → US/UK conversion (same as variant-size-system.js).
 */
const SIZE_MAP = {
  33:   { us: '3',    uk: '1' },
  33.5: { us: '3.5',  uk: '1.5' },
  34:   { us: '4',    uk: '1.5' },
  34.5: { us: '4.5',  uk: '2' },
  35:   { us: '5',    uk: '2.5' },
  35.5: { us: '5.5',  uk: '3' },
  36:   { us: '5.5',  uk: '3.5' },
  36.5: { us: '6',    uk: '3.5' },
  37:   { us: '6',    uk: '4' },
  37.5: { us: '6.5',  uk: '4.5' },
  38:   { us: '7',    uk: '5' },
  38.5: { us: '7.5',  uk: '5.5' },
  39:   { us: '7.5',  uk: '6' },
  39.5: { us: '8',    uk: '6.5' },
  40:   { us: '8.5',  uk: '6.5' },
  40.5: { us: '9',    uk: '7' },
  41:   { us: '9.5',  uk: '7.5' },
  41.5: { us: '10',   uk: '8' },
  42:   { us: '10.5', uk: '8.5' },
  42.5: { us: '11',   uk: '9' },
  43:   { us: '11.5', uk: '9.5' },
  43.5: { us: '12',   uk: '10' },
  44:   { us: '12',   uk: '10' },
  44.5: { us: '12.5', uk: '10.5' },
  45:   { us: '13',   uk: '11' },
  45.5: { us: '13.5', uk: '11.5' },
  46:   { us: '14',   uk: '12' },
  46.5: { us: '14.5', uk: '12.5' },
  47:   { us: '15',   uk: '13' },
  47.5: { us: '15.5', uk: '13.5' },
  48:   { us: '16',   uk: '14' },
  48.5: { us: '16.5', uk: '14.5' },
  49:   { us: '17',   uk: '15' },
  50:   { us: '18',   uk: '16' },
};

/**
 * Size guide picker component — EU/US/UK switching + foot length display.
 * Lives inside the generic estro-drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [footValue]
 *
 * @extends {Component<Refs>}
 */
class SizeGuidePicker extends Component {
  /** @type {string} */
  #activeSystem = 'eu';

  connectedCallback() {
    super.connectedCallback();

    this.querySelectorAll('[data-guide-system]').forEach((tab) => {
      tab.addEventListener('click', this.#handleSystemChange);
    });

    this.querySelectorAll('[data-guide-variant-id]').forEach((btn) => {
      btn.addEventListener('click', this.#handleSizeClick);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.querySelectorAll('[data-guide-system]').forEach((tab) => {
      tab.removeEventListener('click', this.#handleSystemChange);
    });

    this.querySelectorAll('[data-guide-variant-id]').forEach((btn) => {
      btn.removeEventListener('click', this.#handleSizeClick);
    });
  }

  /**
   * Handle EU/US/UK tab switching.
   * @param {MouseEvent} event
   */
  #handleSystemChange = (event) => {
    const tab = /** @type {HTMLElement} */ (event.currentTarget);
    const system = tab.dataset.guideSystem;
    if (!system) return;

    this.#activeSystem = system;

    this.querySelectorAll('[data-guide-system]').forEach((t) => {
      t.classList.toggle('size-guide__system-tab--active', t.dataset.guideSystem === system);
    });

    this.querySelectorAll('[data-guide-eu-value]').forEach((btn) => {
      const euVal = btn.dataset.guideEuValue;
      const textSpan = btn.querySelector('.size-guide__size-btn-text');
      if (!textSpan) return;

      if (system === 'eu') {
        textSpan.textContent = euVal;
      } else {
        const converted = SIZE_MAP[parseFloat(euVal)];
        textSpan.textContent = converted ? converted[system] : euVal;
      }
    });
  };

  /**
   * Handle size button click — show foot length for that variant.
   * @param {MouseEvent} event
   */
  #handleSizeClick = (event) => {
    const btn = /** @type {HTMLElement} */ (event.currentTarget);

    this.querySelectorAll('[data-guide-variant-id]').forEach((b) => b.classList.remove('size-guide__size-btn--active'));
    btn.classList.add('size-guide__size-btn--active');

    const footLength = btn.dataset.guideFootLength;
    const footValue = this.refs.footValue;
    if (footValue) {
      footValue.textContent = footLength || '';
    }
  };
}

if (!customElements.get('size-guide-picker')) {
  customElements.define('size-guide-picker', SizeGuidePicker);
}
